'use client';

import { useState } from 'react';

import {
  MOBILE_APP_LOCATION_PRESETS,
  type MobileAppDebugLogEntry,
  type MobileAppSession,
  type MobileAppSessionPhase,
} from '@/shared-mobile-app';
import { Button } from '@/shared-ui';

import ProjectUsabilityMobileAppDebugLogRow from './project-usability-mobile-app-debug-log-row';
import ProjectUsabilityMobileAppDebugStat from './project-usability-mobile-app-debug-stat';

interface Props {
  entries: MobileAppDebugLogEntry[];
  onClear: () => void;
  sdkStatus: string;
  sessionPhase: MobileAppSessionPhase;
  queueText: string | null;
  buildId: string;
  micStatus: string;
  micLevel: number;
  session: MobileAppSession | null;
  onLog: (
    source: MobileAppDebugLogEntry['source'],
    label: string,
    detail?: unknown
  ) => void;
}

export default function ProjectUsabilityMobileAppDebug({
  entries,
  onClear,
  sdkStatus,
  sessionPhase,
  queueText,
  buildId,
  micStatus,
  micLevel,
  session,
  onLog,
}: Props) {
  const [collapsed, setCollapsed] = useState(false);
  const [tapX, setTapX] = useState('200');
  const [tapY, setTapY] = useState('400');
  const [deepLink, setDeepLink] = useState('');
  const [textToType, setTextToType] = useState('');

  async function setLocation(lat: number, lng: number, label: string) {
    if (!session) {
      onLog('session', 'No active session for setLocation');
      return;
    }
    try {
      onLog('session', `setLocation → ${label}`, { lat, lng });
      await session.setLocation(lat, lng);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      onLog('session', 'setLocation failed', { message });
    }
  }

  async function tap() {
    if (!session) return;
    const x = Number.parseInt(tapX, 10);
    const y = Number.parseInt(tapY, 10);
    if (Number.isNaN(x) || Number.isNaN(y)) return;
    try {
      onLog('session', `tap (${x}, ${y})`);
      await session.tap(x, y);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      onLog('session', 'tap failed', { message });
    }
  }

  async function openUrl() {
    if (!session || !deepLink) return;
    try {
      onLog('session', `openUrl → ${deepLink}`);
      await session.openUrl(deepLink);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      onLog('session', 'openUrl failed', { message });
    }
  }

  async function typeText() {
    if (!session || !textToType) return;
    try {
      onLog('session', `type "${textToType}"`);
      await session.type(textToType);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      onLog('session', 'type failed', { message });
    }
  }

  async function endSession() {
    if (!session) return;
    try {
      onLog('session', 'manual end()');
      await session.end();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      onLog('session', 'end failed', { message });
    }
  }

  return (
    <aside
      data-test='mobile-app-debug-panel'
      data-collapsed={collapsed}
      aria-label='Mobile App user-test debug panel'
      className='pointer-events-auto absolute right-3 bottom-3 z-30 flex w-80 max-w-[90vw] flex-col gap-2 rounded-lg border border-white/10 bg-black/80 p-2 text-[11px] text-white shadow-xl backdrop-blur data-[collapsed=true]:w-40'
    >
      <header className='flex items-center justify-between'>
        <strong className='font-mono tracking-wide uppercase'>Debug</strong>
        <Button
          variant='ghost'
          data-test='mobile-app-debug-toggle'
          onClick={() => setCollapsed((v) => !v)}
          aria-label={collapsed ? 'Expand debug panel' : 'Collapse debug panel'}
          aria-expanded={!collapsed}
          className='h-auto p-0 text-white/60 focus-visible:outline-1 focus-visible:outline-white/40 hover:enabled:bg-transparent hover:enabled:text-white'
        >
          <span aria-hidden='true'>{collapsed ? '▲' : '▼'}</span>
        </Button>
      </header>

      {collapsed ? null : (
        <>
          <section className='grid grid-cols-2 gap-1'>
            <ProjectUsabilityMobileAppDebugStat label='SDK' value={sdkStatus} />
            <ProjectUsabilityMobileAppDebugStat
              label='Phase'
              value={sessionPhase}
            />
            <ProjectUsabilityMobileAppDebugStat
              label='Queue'
              value={queueText ?? '—'}
            />
            <ProjectUsabilityMobileAppDebugStat
              label='Build'
              value={buildId.slice(0, 12) + '…'}
            />
            <ProjectUsabilityMobileAppDebugStat label='Mic' value={micStatus} />
            <ProjectUsabilityMobileAppDebugStat
              label='Level'
              value={`${(micLevel * 100).toFixed(0)}%`}
            />
          </section>

          <section className='flex flex-col gap-1 rounded border border-white/5 p-1.5'>
            <strong className='text-[9px] tracking-wide text-white/60 uppercase'>
              Session controls
            </strong>
            <div className='flex flex-wrap gap-1'>
              {MOBILE_APP_LOCATION_PRESETS.map((p) => (
                <Button
                  variant='ghost'
                  key={p.label}
                  onClick={() => setLocation(p.lat, p.lng, p.label)}
                  aria-label={`Set location to ${p.label}`}
                  className='h-auto rounded border border-white/15 px-2 py-0.5 text-[10px] text-white focus-visible:outline-1 focus-visible:outline-white/40 hover:enabled:bg-white/10'
                >
                  <span aria-hidden='true'>📍 </span>
                  {p.label}
                </Button>
              ))}
            </div>
            <div className='flex items-center gap-1'>
              <input
                value={tapX}
                onChange={(e) => setTapX(e.target.value)}
                aria-label='Tap x coordinate'
                data-test='mobile-app-debug-tap-x'
                className='w-12 rounded bg-white/10 px-1.5 py-0.5'
                placeholder='x'
              />
              <input
                value={tapY}
                onChange={(e) => setTapY(e.target.value)}
                aria-label='Tap y coordinate'
                data-test='mobile-app-debug-tap-y'
                className='w-12 rounded bg-white/10 px-1.5 py-0.5'
                placeholder='y'
              />
              <Button size='sm' variant='secondary' onClick={tap}>
                Tap
              </Button>
            </div>
            <div className='flex items-center gap-1'>
              <input
                value={deepLink}
                onChange={(e) => setDeepLink(e.target.value)}
                aria-label='Deep link URL'
                data-test='mobile-app-debug-deeplink'
                className='flex-1 rounded bg-white/10 px-1.5 py-0.5'
                placeholder='myapp://path'
              />
              <Button size='sm' variant='secondary' onClick={openUrl}>
                Open
              </Button>
            </div>
            <div className='flex items-center gap-1'>
              <input
                value={textToType}
                onChange={(e) => setTextToType(e.target.value)}
                aria-label='Text to type into app'
                data-test='mobile-app-debug-type-text'
                className='flex-1 rounded bg-white/10 px-1.5 py-0.5'
                placeholder='Text to type'
              />
              <Button size='sm' variant='secondary' onClick={typeText}>
                Type
              </Button>
            </div>
            <Button size='sm' variant='destructive' onClick={endSession}>
              End session
            </Button>
          </section>

          <section className='flex flex-col gap-1 rounded border border-white/5 p-1.5'>
            <header className='flex items-center justify-between'>
              <strong className='text-[9px] tracking-wide text-white/60 uppercase'>
                Event log ({entries.length})
              </strong>
              <Button
                variant='ghost'
                onClick={onClear}
                aria-label='Clear event log'
                className='h-auto p-0 text-[10px] text-white/50 focus-visible:outline-1 focus-visible:outline-white/40 hover:enabled:bg-transparent hover:enabled:text-white'
              >
                Clear
              </Button>
            </header>
            <ol className='max-h-56 overflow-y-auto font-mono leading-tight'>
              {entries.length === 0 ? (
                <li className='py-1 text-white/40'>No events yet.</li>
              ) : (
                entries.map((e) => (
                  <ProjectUsabilityMobileAppDebugLogRow key={e.id} entry={e} />
                ))
              )}
            </ol>
          </section>
        </>
      )}
    </aside>
  );
}
